import Phaser from 'phaser';

import { cursorCSS, VERSION } from 'helpers/constants';
import { reportClientError } from 'helpers/server_connection';

import BootScene from './states/menus/Boot';
import LoadingScene from './states/menus/Loading';
import GameMenuScene from './states/menus/GameMenu';
import GameScene from './states/Game';
import DialogueScene from './ui/dialogue';
import PieceInfoScene from './ui/pieceInfo';
import SharedPartyMenuStub from './states/components/SharedPartyMenuStub';
import OptionsMainMenu from './states/components/OptionsMainMenu';
import ZoomControls from './states/components/ZoomControls';
import StoryResults from './ui/storyResults';
import HighscoreModal from './ui/highscoreModal';

import { Plugin as NineSlicePlugin } from 'phaser3-nineslice';

const getOrCreateAnonymousUser = () => {
  const key = 'chesslike-shared-anonymous-id';
  const existing = window.localStorage?.getItem(key);
  if (existing) return existing;

  const id =
    window.crypto?.randomUUID?.() || `shared-${Date.now()}-${Math.random()}`;
  window.localStorage?.setItem(key, id);
  return id;
};

const getErrorReporterSessionId = () => {
  if (window.game?.sessionID) return window.game.sessionID;
  if (window.__clErrorReporterSessionId)
    return window.__clErrorReporterSessionId;

  window.__clErrorReporterSessionId =
    window.crypto?.randomUUID?.() || `startup-${Date.now()}`;
  return window.__clErrorReporterSessionId;
};

const serializeErrorValue = value => {
  if (value instanceof Error) {
    return {
      error_name: value.name,
      message: value.message,
      stack: value.stack
    };
  }

  return {
    error_name: typeof value,
    message: String(value),
    stack: ''
  };
};

const getActiveSceneName = () => {
  try {
    return (window.game?.scene?.getScenes(true) || [])
      .map(scene => scene.scene?.key)
      .filter(Boolean)
      .join(',');
  } catch (error) {
    return '';
  }
};

const installGlobalErrorReporter = () => {
  const reportedKeys = new Set();

  const report = (value, extra = {}) => {
    const serialized = serializeErrorValue(value);
    const key = `${serialized.message}|${serialized.stack}`;
    if (reportedKeys.has(key)) return;
    reportedKeys.add(key);

    reportClientError({
      user_id: window.game?.player?.name || getOrCreateAnonymousUser(),
      session_id: getErrorReporterSessionId(),
      platform: 'shared-web',
      build_version: __COMMIT_HASH__,
      scene: getActiveSceneName(),
      url: window.location.href,
      ...serialized,
      metadata: JSON.stringify({
        mode: 'shared_level',
        ...extra
      })
    });
  };

  window.addEventListener('error', event => {
    report(event.error || event.message, { source: 'error' });
  });

  window.addEventListener('unhandledrejection', event => {
    report(event.reason, { source: 'unhandledrejection' });
  });
};

installGlobalErrorReporter();

try {
  if (window.Worker) {
    window.aiWorker = new Worker(new URL('./ai.worker.js', import.meta.url));
  }
} catch (error) {
  console.log('error loading window worker:', error);
}

const config = {
  type: Phaser.WEBGL,
  parent: 'content',
  width: 1280,
  height: 720,
  resolution: window.devicePixelRatio,
  roundPixels: true,
  antialias: false,
  antialiasGL: false,
  pixelArt: true,
  render: {
    roundPixels: true,
    antialias: false,
    antialiasGL: false,
    pixelArt: true
  },
  scale: {
    mode: Phaser.Scale.RESIZE,
    baseSize: { width: 1280, height: 720 },
    parent: 'content',
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: 1280,
    height: 720
  },
  dom: {
    createContainer: true
  },
  plugins: {
    global: [NineSlicePlugin.DefaultCfg]
  },
  input: {
    gamepad: true
  },
  autoResize: true,
  scene: [
    BootScene,
    LoadingScene,
    GameMenuScene,
    GameScene,
    DialogueScene,
    PieceInfoScene,
    SharedPartyMenuStub,
    OptionsMainMenu,
    ZoomControls,
    StoryResults,
    HighscoreModal
  ]
};

const Game = new Phaser.Game(config);
Game.sound.pauseOnBlur = false;
window.game = Game;

const anonymousUserId = getOrCreateAnonymousUser();
Game.player = {
  name: anonymousUserId
};
Game.sessionID =
  window.crypto?.randomUUID?.() || `shared-session-${Date.now()}`;
window.localStorage?.setItem('chesslike-user', anonymousUserId);
window.localStorage?.setItem('chesslike-userid', anonymousUserId);
window.localStorage?.setItem('cl-ver', VERSION);

window.ai = {
  method: 'minimax',
  'future looking limit': 3,
  debug: false
};

Game.animatedWorld = false;

const BREAKS = {
  WIDTH: [1200, 1920],
  HEIGHT: [600, 900]
};

const updateDefaultZoom = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const scalar = Math.max(width, height);
  const isPortrait = scalar === height;
  const METRIC = 'WIDTH';

  let zoom;
  if (scalar < BREAKS[METRIC][0]) {
    zoom = 1;
  } else if (scalar >= BREAKS[METRIC][0] && scalar < BREAKS[METRIC][1]) {
    zoom = 2;
  } else if (scalar >= BREAKS[METRIC][1]) {
    zoom = 3;
  }

  Game.isPortrait = isPortrait;
  Game.defaultZoom = zoom;
  document.body.classList.remove('zoom-1', 'zoom-2', 'zoom-3');
  document.body.classList.add(`zoom-${zoom}`);
};

updateDefaultZoom();
window.addEventListener('resize', updateDefaultZoom);
document.body.style.cursor = cursorCSS[Game.defaultZoom];

Game.defaultMovementDuration = 300;
Game.movementAcceleration = 100;
Game.minimumMoveDuration = 100;
